import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Layout from "../components/Layout";
import NextButton from "../components/NextButton";
import type { StepComponentType } from "../../hooks/IncorporationWizardFormContext";
import { useIncorporationWizardFormCtx } from "../../hooks/IncorporationWizardFormContext";
import TextInput from "../../components/TextInput";
import RadioGroup from "../../components/RadioGroup";
import PhoneInput from "../../components/PhoneInput";
import {
  USER_TYPE,
  type IncorporationWizardInitialData,
  type UserTypeOption,
} from "@/schemas/incorporation";
import {
  createOrUpdateIncorporationRequest,
  IncorporationRequestError,
} from "@/services/incorporationRequest";

const [miembro, intermediario] = USER_TYPE;

// Base schema for member (no additional fields needed)
const miembroSchema = z.object({
  user_type: z.literal(miembro),
});

// Schema for intermediary (all fields required)
const intermediarioSchema = z.object({
  user_type: z.literal(intermediario),
  intermediario_nombre: z
    .string({ error: "field_required" })
    .trim()
    .min(1, "user_type.form.full_name.required"),
  intermediario_email: z
    .string({ error: "field_required" })
    .min(1, "user_type.form.email.required")
    .email("user_type.form.email.invalid"),
  intermediario_telefono: z
    .string({ error: "field_required" })
    .min(1, "user_type.form.phone.required")
    .e164({ error: "user_type.form.phone.invalid" }),
});

const baseSchema = z.object({
  user_type: z
    .string({ error: "field_required" })
    .min(1, "user_type.validation.role_required")
    .refine(
      (value): value is UserTypeOption =>
        value === miembro || value === intermediario,
      {
        message: "user_type.validation.role_required",
      }
    ),
});

// Then apply the discriminated union validation
const userTypeSchema = baseSchema.and(
  z.discriminatedUnion("user_type", [miembroSchema, intermediarioSchema])
);

// Type for step data (allows partial data during form filling)
type UserTypeStepData = z.infer<typeof userTypeSchema>;

const UserTypeStep: StepComponentType = () => {
  const { t } = useTranslation();
  const { data, setData, next } = useIncorporationWizardFormCtx();
  const [saveError, setSaveError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    watch,
    setError: setFormError,
    formState: { isSubmitting },
  } = useForm<UserTypeStepData>({
    resolver: zodResolver(userTypeSchema),
    defaultValues: {
      user_type: data?.incorporationRequest?.user_type,
      intermediario_nombre:
        data?.incorporationRequest?.intermediario_nombre || "",
      intermediario_email:
        data?.incorporationRequest?.intermediario_email || "",
      intermediario_telefono:
        data?.incorporationRequest?.intermediario_telefono || "",
    },
  });

  const selectedUserType = watch("user_type");
  const isIntermediary = selectedUserType === intermediario;

  // User type options for RadioGroup
  const userTypeOptions = [
    {
      value: miembro,
      label: t("user_type.options.member"),
      description: t("user_type.options.member_description"),
    },
    {
      value: intermediario,
      label: t("user_type.options.intermediary"),
      description: t("user_type.options.intermediary_description"),
      children: (
          isIntermediary ? (
            <div className="d-flex flex-column gap-3 p-4">
              <TextInput
                name="intermediario_nombre"
                control={control}
                label={t("user_type.form.full_name.label")}
                placeholder={t("user_type.form.full_name.placeholder")}
              />
              <TextInput
                name="intermediario_email"
                control={control}
                label={t("user_type.form.email.label")}
                placeholder={t("user_type.form.email.placeholder")}
              />
              <PhoneInput
                name="intermediario_telefono"
                control={control}
                label={t("user_type.form.phone.label")}
                placeholder={t("user_type.form.phone.placeholder")}
              />
            </div>
          ) : null
      ),
    },
  ];

  const onSubmit = async (formData: UserTypeStepData) => {
    setSaveError(null);

    try {
      // The incorporation request should already exist at this point (created in company-name-step)
      const incorporationRequestId = data?.incorporationRequest?.id;

      if (!incorporationRequestId) {
        throw new Error(
          "La solicitud de incorporación no ha sido creada. Por favor, complete el paso anterior."
        );
      }

      const requestData = {
        ...data?.incorporationRequest,
        ...formData,
      };

      const response = await createOrUpdateIncorporationRequest(requestData);

      setData((prevData) => {
        if (!prevData) return prevData;
        return {
          ...prevData,
          incorporationRequest: {
            ...prevData.incorporationRequest,
            ...response.data,
            id: response.id,
          },
          incorporationContact: prevData.incorporationContact || [],
          currentUser: prevData.currentUser,
          incorporationStripePrice: prevData.incorporationStripePrice,
          path: prevData.path,
        };
      });

      // Navigate to next step on success
      next();
    } catch (error) {
      // Handle different error types
      if (error instanceof IncorporationRequestError) {
        const errorMessage =
          error.errors && error.errors.length > 0
            ? error.errors.join(", ")
            : error.message;
        setSaveError(errorMessage);

        // For validation errors (422), try to set field-specific errors if possible
        if (error.statusCode === 422 && error.errors) {
          // Set a general form error - could be enhanced to map to specific fields
          setFormError("root", { message: errorMessage });
        }
      } else {
        const errorMessage =
          error instanceof Error ? error.message : "Error al guardar los datos";
        setSaveError(errorMessage);
        setFormError("root", { message: errorMessage });
      }
      // Re-throw to prevent navigation on error
      throw error;
    }
  };

  return (
    <Layout
      title={t("user_type.title")}
      description={t("user_type.description")}
      maxWidth={600}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div
          className="d-flex flex-column gap-3 text-start m-auto mt-4"
          style={{ maxWidth: "500px" }}
        >
          {/* User Type Radio Group */}
          <RadioGroup
            name="user_type"
            control={control}
            options={userTypeOptions}
            layout="vertical"
          />

          {/* Error Message */}
          {saveError && (
            <div className="alert alert-danger" role="alert">
              {saveError}
            </div>
          )}

          {/* Continue Button */}
          <div className="d-flex justify-content-center">
            <NextButton
              type="submit"
              label={t("user_type.continue")}
              disabled={isSubmitting}
            />
          </div>
        </div>
      </form>
    </Layout>
  );
};

UserTypeStep.title = (t) => t("user_type.progress_title");
UserTypeStep.description = (t) => t("user_type.description_progress_panel");
UserTypeStep.isComplete = (data: IncorporationWizardInitialData) => {
  const details = userTypeSchema.safeParse(data.incorporationRequest);
  return details.success;
};

export default UserTypeStep;
